import React, { useState, useEffect } from "react";
import { FaEdit, FaTrashAlt, FaPlus } from "react-icons/fa";
import Input from "../../../DynamicComp/Input";
import TextBox from "../../../DynamicComp/TextBox";
import Button from "../../../DynamicComp/Button";
import GenericTable from "../../../DynamicComp/Table";
import { showAlert } from "../../../DynamicComp/Alert";

import {
  deleteData,
  getData,
  postData,
  putData,
} from "../../../../global/Globalapi";

const Features = () => {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [component_data, setData] = useState([]);
  const [formData, setFormData] = useState({
    attribute_name: "",
    attribute_description: "",
    attribute_field_type_id: "",
    isVariant: false,
  });
  const [activeTab, setActiveTab] = useState("static");

  const fetchData = async () => {
    try {
      const response = await getData("/component");
      // Filter data based on active tab
      const filteredData = response.data.filter(item => 
        activeTab === "variant" ? item.isVariant : !item.isVariant
      );
      setData(filteredData);
    } catch (error) {
      console.error("Error fetching data:", error);
      showAlert({
        type: "error",
        message: "Failed to fetch data",
        title: "Error",
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const columns = [
    {
      key: "sno",
      label: "S.No",
      sortable: false,
      align: "center",
      width: "80px",
    },
    { key: "attribute_name", label: "Attribute Name", sortable: false },
    {
      key: "attributeType",
      label: "Attribute Type",
      sortable: false,
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            row.isVariant
              ? "bg-purple-100 text-purple-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {row.isVariant ? "Variant" : "Static"}
        </span>
      ),
    },
    { 
      key: "attribute_field_type_id", 
      label: "Input Type", 
      sortable: false,
      render: (row) => {
        const typeMap = {
          "1": "Text Field",
          "2": "Number",
          "3": "Dropdown",
          "4": "Checkbox",
          "5": "Radio Button"
        };
        return typeMap[row.attribute_field_type_id] || row.attribute_field_type_id;
      }
    },
    {
      key: "actions",
      label: "Actions",
      align: "center",
      width: "120px",
    },
  ];

  const customRenderers = {
    actions: (row) => (
      <div className="flex gap-2 justify-center">
        <button
          className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
          onClick={() => handleEdit(row)}
        >
          <FaEdit />
        </button>
        <button
          className="bg-red-700 text-white px-2 py-1 rounded hover:bg-red-600"
          onClick={() => handleDelete(row?.attribute_id)}
        >
          <FaTrashAlt />
        </button>
      </div>
    ),
  };

  const handleCancel = () => {
    setFormData({
      attribute_name: "",
      attribute_description: "",
      attribute_field_type_id: "",
      isVariant: false,
    });
    setEditId(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEdit = (comp) => {
    setFormData({
      attribute_name: comp.attribute_name,
      attribute_description: comp.attribute_description || "",
      attribute_field_type_id: String(comp.attribute_field_type_id) || "",
      isVariant: comp.isVariant || false,
    });
    setEditId(comp.attribute_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this attribute?"))
      return;

    try {
      await deleteData(`/del_comp/${id}`);
      showAlert({
        type: "success",
        message: "Attribute deleted successfully ✅",
        title: "Success",
      });
      fetchData();
    } catch (error) {
      showAlert({
        type: "error",
        message: "Failed to delete attribute ❌",
        title: "Error",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validation
      if (!formData.attribute_name || !formData.attribute_field_type_id) {
        showAlert({
          type: "warning",
          message: "Attribute name and input type are required",
          title: "Warning",
        });
        return;
      }

      // Check for duplicate names (excluding current item if editing)
      const existingComp = component_data.find(
        (comp) =>
          comp.attribute_name.toLowerCase() === formData.attribute_name.toLowerCase() &&
          comp.attribute_id !== editId
      );

      if (existingComp) {
        showAlert({
          type: "warning",
          message: "This attribute name already exists",
          title: "Warning",
        });
        return;
      }

      const payload = {
        ...formData,
        isVariant: activeTab === "variant",
        attribute_field_type_id: parseInt(formData.attribute_field_type_id)
      };

      if (editId) {
        const response = await putData(`/comp_update/${editId}`, payload);
        showAlert({
          type: "success",
          message: response.data.message || "Attribute updated successfully",
          title: "Success",
        });
      } else {
        const response = await postData("/component_add", payload);
        showAlert({
          type: "success",
          message: response.data.message || "Attribute added successfully",
          title: "Success",
        });
      }

      fetchData();
      handleCancel();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Operation failed";
      showAlert({
        type: "error",
        message: errorMessage,
        title: "Error",
      });
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen w-full p-8 bg-gray-50">
      <div className="bg-white p-6 rounded-xl shadow-lg w-full overflow-x-auto mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-black flex-grow">
            Product Attributes
          </h2>
          <div className="flex space-x-4">
            <Button
              variant="danger"
              onClick={() => {
                setEditId(null);
                setShowForm(true);
                setFormData({
                  attribute_name: "",
                  attribute_description: "",
                  attribute_field_type_id: "",
                  isVariant: activeTab === "variant",
                });
              }}
              className="p-2 rounded-lg flex items-center space-x-2"
            >
              <FaPlus />
              <span>Add Attribute</span>
            </Button>
          </div>
        </div>

        <div className="flex mb-4 border-b">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "static"
                ? "text-red-700 border-b-2 border-red-700"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("static")}
          >
            Static Attributes
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "variant"
                ? "text-red-700 border-b-2 border-red-700"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("variant")}
          >
            Variant Attributes
          </button>
        </div>

        <div className="w-full border-collapse">
          <GenericTable
            columns={columns}
            data={component_data}
            customRenderers={customRenderers}
            headerClassName="bg-red-700 text-white"
            rowKey="attribute_id"
            stripedRows={true}
            hoverEffect={true}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </div>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-xl shadow-lg w-full">
          <h2 className="text-2xl font-bold text-left mb-4 text-black">
            {editId
              ? "Edit Attribute"
              : `Add ${activeTab === "variant" ? "Variant" : "Static"} Attribute`}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1">
                <Input
                  type="text"
                  label="Attribute Name *"
                  name="attribute_name"
                  value={formData.attribute_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter attribute name"
                />
              </div>

              <div className="col-span-1">
                <label className="block text-gray-700 font-semibold mb-2">
                  Input Type *
                </label>
                <select
                  name="attribute_field_type_id"
                  value={formData.attribute_field_type_id}
                  onChange={handleChange}
                  required
                  className="block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Input Type</option>
                  <option value="1">Text Field</option>
                  <option value="2">Number</option>
                  <option value="3">Dropdown</option>
                  <option value="4">Checkbox</option>
                  <option value="5">Radio Button</option>
                </select>
              </div>
            </div>

            <div>
              <TextBox
                label="Description"
                name="attribute_description"
                value={formData.attribute_description}
                onChange={handleChange}
                rows={3}
                placeholder="Enter description..."
              />
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                variant="danger"
                type="submit"
                className="px-6 py-2 rounded-lg"
              >
                {editId ? "Update" : "Add"}
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 rounded-lg"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Features;